import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  PORTAL_APPEARANCE_CHANGED,
  loadPortalAppearance,
  savePortalAppearance,
  type PortalAppearance,
} from './portalAppearance';

type PortalAppearanceContextValue = {
  appearance: PortalAppearance;
  setAppearance: (next: PortalAppearance) => void;
};

const PortalAppearanceContext = createContext<PortalAppearanceContextValue>({
  appearance: 'dark',
  setAppearance: () => {},
});

export const PortalAppearanceProvider: React.FC<{ seatId: string; children: React.ReactNode }> = ({
  seatId,
  children,
}) => {
  const [appearance, setAppearanceState] = useState<PortalAppearance>(() =>
    typeof window === 'undefined' ? 'dark' : loadPortalAppearance(window.localStorage, seatId),
  );

  useEffect(() => {
    const read = () => setAppearanceState(loadPortalAppearance(window.localStorage, seatId));
    read();
    window.addEventListener(PORTAL_APPEARANCE_CHANGED, read);
    return () => window.removeEventListener(PORTAL_APPEARANCE_CHANGED, read);
  }, [seatId]);

  const setAppearance = useCallback(
    (next: PortalAppearance) => {
      const storage = typeof window === 'undefined' ? null : window.localStorage;
      savePortalAppearance(storage, seatId, next);
      setAppearanceState(loadPortalAppearance(storage, seatId));
    },
    [seatId],
  );

  return (
    <PortalAppearanceContext.Provider value={{ appearance, setAppearance }}>
      {children}
    </PortalAppearanceContext.Provider>
  );
};

export function usePortalAppearance(): PortalAppearance {
  return useContext(PortalAppearanceContext).appearance;
}

export function useSetPortalAppearance(): (next: PortalAppearance) => void {
  return useContext(PortalAppearanceContext).setAppearance;
}

/** Eine Fläche der Schale, nur hell wenn Hell gewählt ist. Kein iframe. */
export const PortalLightSurface: React.FC<{
  surface: (appearance: PortalAppearance) => string;
  className: string;
  testId?: string;
  children: React.ReactNode;
}> = ({ surface, className, testId, children }) => {
  const mark = surface(usePortalAppearance());
  return (
    <div className={mark ? `${className} ${mark}` : className} data-testid={testId}>
      {children}
    </div>
  );
};
